import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AUTH_PROVIDERS } from '@ewentscompany/ewents-settings';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { buildEndpointUrl, SERVICES, SYSTEM_PLATFORMS } from '../shared/constants/api.constants';
import type { UserContact } from '../users/user.model';

export type AuthProviderType = (typeof AUTH_PROVIDERS)[keyof typeof AUTH_PROVIDERS];

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  contactId?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  birthday?: string;
  personalEmail?: string;
  address?: string;
  floor?: string;
  details?: string;
  country?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  contact?: UserContact | null;
  isActive: boolean;
  lastLoginAt?: string;
  roles?: any[];
  allPermissions?: string[];
  platforms?: string[]; // Added for platform access check
  authProvider?: AuthProviderType;
  totpEnabled?: boolean;
}

/**
 * FE-L2: what survives in localStorage/sessionStorage. Name / email / avatar for the
 * shell, plus permissions / platforms / 2FA flag as UI hints (the API re-checks every
 * call). The full profile (phone, birthday, address, contact, …) stays in tab memory
 * and is re-read from `GET /users/me` after a reload.
 */
export type CachedUserProfile = Pick<
  User,
  | 'id'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'avatarUrl'
  | 'contactId'
  | 'isActive'
  | 'platforms'
  | 'allPermissions'
  | 'totpEnabled'
  | 'authProvider'
>;

export function toCachedUserProfile(user: User): CachedUserProfile {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    avatarUrl: user.avatarUrl ?? user.contact?.avatarUrl ?? undefined,
    contactId: user.contactId,
    isActive: user.isActive,
    platforms: user.platforms,
    allPermissions: user.allPermissions,
    totpEnabled: user.totpEnabled,
    authProvider: user.authProvider,
  };
}

/** Session is the HttpOnly product cookie; the body never carries a token. */
export interface LoginResponse {
  user: User;
  requires2FA?: boolean;
}

type AuthChannelMessage =
  { type: 'request' } | { type: 'logout' } | { type: 'user'; user: User; remember: boolean };

@Injectable({ providedIn: 'root' })
export class LoginService {
  private readonly userKey = 'ewents_user';
  private loggedIn$ = new BehaviorSubject<boolean>(this.isAuthenticated());
  /**
   * Remember device → cookie Max-Age + profile in localStorage.
   * Off → session cookie + sessionStorage; other tabs via BroadcastChannel / ensureSession → `/me`.
   */
  private readonly rememberKey = 'ewents_remember';
  /** Preserve explicit logout intent even if clearing the HttpOnly cookie is offline. */
  private readonly logoutKey = 'ewents_logged_out';
  private readonly authChannelName = 'ewents-auth-crm';
  private authChannel: BroadcastChannel | null = null;
  /** Full `/me` profile for this tab only (never persisted). */
  private memoryUser: User | null = null;
  /** One background `/me` per tab when only the minimal cached profile is available. */
  private profileHydrationStarted = false;
  private currentUser$ = new BehaviorSubject<User | null>(this.getUser());
  /** Bumped on logout so late profile responses cannot restore a cleared session. */
  private sessionEpoch = 0;
  /** Lazy MatDialog — avoid field-inject circular DI with AuthInterceptor → HttpClient. */
  private readonly injector = inject(Injector);

  constructor(
    private router: Router,
    private http: HttpClient
  ) {
    // Drop any JWT left from pre–fase-2 builds.
    this.initAuthChannel();
  }

  /** Cookie session is the real auth; cached user marks an intentional client session. */
  isAuthenticated(): boolean {
    return !!this.getUser();
  }

  /** Cached profile + CRM platform claim — not a substitute for the HttpOnly cookie. */
  hasUsableSession(): boolean {
    const user = this.getUser();
    if (!user) return false;
    if (!user.platforms || user.platforms.length === 0) return false;
    return [SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
      (user.platforms ?? []).includes(p)
    );
  }

  /**
   * Restore client profile for this tab: cache, sibling broadcast, or `/me` via cookie.
   * AuthGuard uses this so ephemeral sessions work across tabs without localStorage.
   * Soft probe: failure does not broadcast logout (other tabs may still be fine).
   */
  ensureSession(): Observable<boolean> {
    if (this.hasUsableSession()) {
      this.hydrateProfileInBackground();
      return of(true);
    }
    return this.fetchCurrentUser().pipe(
      map(() => this.hasUsableSession()),
      catchError(() => of(false))
    );
  }

  private isRememberedSession(): boolean {
    return localStorage.getItem(this.rememberKey) === '1';
  }

  /**
   * Cached UI profile (storage, see `CachedUserProfile`) layered over the full `/me`
   * profile held in tab memory. Storage stays the source of "is there a client session"
   * and of the UI hints (permissions / platforms / 2FA), exactly as before FE-L2; memory
   * only adds the profile details that are no longer persisted.
   */
  getUser(): User | null {
    const cached = this.readCachedProfile();
    if (!cached) {
      this.memoryUser = null;
      return null;
    }
    if (this.memoryUser && this.memoryUser.id === cached.id) {
      return { ...this.memoryUser, ...cached };
    }
    // Minimal profile: optional profile fields are absent until `/me` hydrates memory.
    return cached as User;
  }

  private readCachedProfile(): CachedUserProfile | null {
    const raw = sessionStorage.getItem(this.userKey) ?? localStorage.getItem(this.userKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as CachedUserProfile | null;
      return parsed && typeof parsed === 'object' && typeof parsed.id === 'string' ? parsed : null;
    } catch {
      // A damaged UI cache must not prevent restoring the HttpOnly session.
      sessionStorage.removeItem(this.userKey);
      localStorage.removeItem(this.userKey);
      return null;
    }
  }

  /**
   * After a reload only the minimal profile is cached: load the full one into tab
   * memory once. Does not rewrite storage, so cached UI hints behave as before.
   */
  private hydrateProfileInBackground(): void {
    if (this.memoryUser || this.profileHydrationStarted) return;
    if (localStorage.getItem(this.logoutKey)) return;
    this.profileHydrationStarted = true;
    const epoch = this.sessionEpoch;
    // Session-ended errors are handled by AuthInterceptor; transient ones keep the cache.
    this.http.get<User>(buildEndpointUrl(SERVICES.USERS, '/me')).subscribe({
      next: user => {
        if (epoch !== this.sessionEpoch) return;
        if (this.readCachedProfile()?.id !== user?.id) return;
        this.memoryUser = user;
        this.currentUser$.next(this.getUser());
      },
      error: () => {},
    });
  }

  getCurrentUser$(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  /** A login succeeded: the HttpOnly session cookie is set; start a fresh client epoch. */
  private beginSession(): void {
    this.sessionEpoch += 1;
    localStorage.removeItem(this.logoutKey);
    this.loggedIn$.next(true);
  }

  getSessionEpoch(): number {
    return this.sessionEpoch;
  }

  /**
   * Cache profile for UI.
   * Remember → localStorage. Ephemeral → sessionStorage (+ BroadcastChannel to sibling tabs).
   */
  setUser(user: User, keepSignedIn = this.isRememberedSession()): void {
    this.persistUser(user, keepSignedIn, true);
  }

  clearToken(): void {
    this.clearTokenLocal();
    this.authChannel?.postMessage({ type: 'logout' } satisfies AuthChannelMessage);
  }

  private clearTokenLocal(): void {
    this.sessionEpoch += 1;
    this.memoryUser = null;
    this.profileHydrationStarted = false;
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.rememberKey);
    sessionStorage.removeItem(this.userKey);
    this.loggedIn$.next(false);
    this.currentUser$.next(null);
  }

  private persistUser(user: User, keepSignedIn: boolean, broadcast: boolean): void {
    this.memoryUser = user;
    const serialized = JSON.stringify(toCachedUserProfile(user));
    if (keepSignedIn) {
      localStorage.setItem(this.userKey, serialized);
      localStorage.setItem(this.rememberKey, '1');
      sessionStorage.removeItem(this.userKey);
    } else {
      sessionStorage.setItem(this.userKey, serialized);
      localStorage.removeItem(this.userKey);
      localStorage.removeItem(this.rememberKey);
    }
    this.currentUser$.next(user);
    this.loggedIn$.next(true);
    if (broadcast) {
      this.authChannel?.postMessage({
        type: 'user',
        user,
        remember: keepSignedIn,
      } satisfies AuthChannelMessage);
    }
  }

  private initAuthChannel(): void {
    if (typeof BroadcastChannel === 'undefined' || this.authChannel) return;
    this.authChannel = new BroadcastChannel(this.authChannelName);
    this.authChannel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'logout') {
        this.clearTokenLocal();
        return;
      }
      if (data.type === 'user' && data.user && !localStorage.getItem(this.logoutKey)) {
        this.persistUser(data.user, !!data.remember, false);
        return;
      }
      if (data.type === 'request') {
        // Only share a full `/me` profile; a tab holding just the minimal cache lets the
        // sibling fetch `/me` itself.
        const user = this.memoryUser ? this.getUser() : null;
        if (!user) return;
        this.authChannel?.postMessage({
          type: 'user',
          user,
          remember: this.isRememberedSession(),
        } satisfies AuthChannelMessage);
      }
    };
    this.authChannel.postMessage({ type: 'request' } satisfies AuthChannelMessage);
  }

  login(
    email: string,
    password: string,
    turnstileToken?: string,
    keepSignedIn = false
  ): Observable<LoginResponse> {
    const loginUrl = buildEndpointUrl(SERVICES.USERS, '/login/crm');

    return this.http
      .post<LoginResponse>(loginUrl, {
        email,
        password,
        keepSignedIn,
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      .pipe(
        tap(response => {
          if (response && response.requires2FA) {
            // Return early if 2FA is required
            return;
          }
          if (response?.user && !response.requires2FA) {
            // Check platform access for CRM
            if (
              !response.user.platforms ||
              ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
                (response.user.platforms ?? []).includes(p)
              )
            ) {
              this.clearToken();
              throw new Error('You do not have access to the CRM platform.');
            }
            this.beginSession();
            this.setUser(response.user, keepSignedIn);
          }
        })
      );
  }

  loginWith2FA(
    email: string,
    password: string,
    totpCode: string,
    turnstileToken?: string,
    keepSignedIn = false
  ): Observable<LoginResponse> {
    const loginUrl = buildEndpointUrl(SERVICES.USERS, '/login/2fa/crm');

    return this.http
      .post<LoginResponse>(loginUrl, {
        email,
        password,
        totpCode,
        keepSignedIn,
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      .pipe(
        tap(response => {
          if (response?.user && !response.requires2FA) {
            // Check platform access for CRM
            if (
              !response.user.platforms ||
              ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
                (response.user.platforms ?? []).includes(p)
              )
            ) {
              this.clearToken();
              throw new Error('You do not have access to the CRM platform.');
            }
            this.beginSession();
            this.setUser(response.user, keepSignedIn);
          }
        })
      );
  }

  loginWithGoogle(
    accessToken: string,
    turnstileToken?: string,
    keepSignedIn = false
  ): Observable<LoginResponse> {
    const loginUrl = buildEndpointUrl(SERVICES.USERS, '/login/google/crm');

    return this.http
      .post<LoginResponse>(loginUrl, {
        accessToken,
        keepSignedIn,
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      .pipe(
        tap(response => {
          if (response && response.requires2FA) {
            // Return early if 2FA is required
            return;
          }
          if (response?.user && !response.requires2FA) {
            // Check platform access for CRM
            if (
              !response.user.platforms ||
              ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
                (response.user.platforms ?? []).includes(p)
              )
            ) {
              this.clearToken();
              throw new Error('You do not have access to the CRM platform.');
            }
            this.beginSession();
            this.setUser(response.user, keepSignedIn);
          }
        })
      );
  }

  loginWithGoogleAnd2FA(
    accessToken: string,
    totpCode: string,
    turnstileToken?: string,
    keepSignedIn = false
  ): Observable<LoginResponse> {
    const loginUrl = buildEndpointUrl(SERVICES.USERS, '/login/google/2fa/crm');

    return this.http
      .post<LoginResponse>(loginUrl, {
        accessToken,
        totpCode,
        keepSignedIn,
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      .pipe(
        tap(response => {
          if (response?.user && !response.requires2FA) {
            // Check platform access for CRM
            if (
              !response.user.platforms ||
              ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
                (response.user.platforms ?? []).includes(p)
              )
            ) {
              this.clearToken();
              throw new Error('You do not have access to the CRM platform.');
            }
            this.beginSession();
            this.setUser(response.user, keepSignedIn);
          }
        })
      );
  }

  /** Discoverable-credential login options for the CRM site. */
  getPasskeyLoginOptions(
    turnstileToken?: string
  ): Observable<PublicKeyCredentialRequestOptionsJSON> {
    return this.http.post<PublicKeyCredentialRequestOptionsJSON>(
      buildEndpointUrl(SERVICES.USERS, '/login/passkey/options/crm'),
      { ...(turnstileToken ? { turnstileToken } : {}) }
    );
  }

  /** Verify a passkey assertion and, on success, apply the CRM session. */
  loginWithPasskey(
    assertion: AuthenticationResponseJSON,
    turnstileToken?: string,
    keepSignedIn = false
  ): Observable<LoginResponse> {
    const verifyUrl = buildEndpointUrl(SERVICES.USERS, '/login/passkey/verify/crm');

    return this.http
      .post<LoginResponse>(verifyUrl, {
        response: assertion,
        keepSignedIn,
        ...(turnstileToken ? { turnstileToken } : {}),
      })
      .pipe(
        tap(response => {
          if (response?.user && !response.requires2FA) {
            if (
              !response.user.platforms ||
              ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
                (response.user.platforms ?? []).includes(p)
              )
            ) {
              this.clearToken();
              throw new Error('You do not have access to the CRM platform.');
            }
            this.beginSession();
            this.setUser(response.user, keepSignedIn);
          }
        })
      );
  }

  logout(): void {
    localStorage.setItem(this.logoutKey, '1');
    this.clearToken();
    const logoutUrl = buildEndpointUrl(SERVICES.USERS, '/logout');
    // Await cookie clear before endSession — otherwise ensureSession()/me rehydrates
    // the profile on the next navigation while the session cookie is still valid.
    this.http.post<{ message: string }>(logoutUrl, {}).subscribe({
      next: () => this.endSession(),
      error: error => {
        console.error('Logout endpoint error (non-critical):', error);
        this.endSession();
      },
    });
  }

  setLoggedOut(): void {
    this.endSession();
  }

  /** Clear session UI (open MatDialogs) then token and route to login. */
  private endSession(): void {
    try {
      this.injector.get(MatDialog, null)?.closeAll();
    } catch {
      /* MatDialog may be unavailable during early bootstrap */
    }
    this.clearToken();
    this.router.navigate(['/login']);
  }

  isLoggedIn$(): Observable<boolean> {
    return this.loggedIn$.asObservable();
  }

  // Fetch current user profile with permissions
  fetchCurrentUser(): Observable<User> {
    if (localStorage.getItem(this.logoutKey)) {
      return throwError(() => new Error('This device was signed out.'));
    }
    const meUrl = buildEndpointUrl(SERVICES.USERS, '/me');
    const epoch = this.sessionEpoch;

    return this.http.get<User>(meUrl).pipe(
      tap(user => {
        if (epoch !== this.sessionEpoch) return;
        // Check platform access for CRM
        if (
          !user.platforms ||
          ![SYSTEM_PLATFORMS.GLOBAL, SYSTEM_PLATFORMS.CRM].some(p =>
            (user.platforms ?? []).includes(p)
          )
        ) {
          this.clearToken();
          throw new Error('You do not have access to the CRM platform.');
        }
        this.setUser(user);
      })
    );
  }

  // Initialize user data if authenticated
  initializeUserData(): void {
    if (this.isAuthenticated()) {
      this.fetchCurrentUser().subscribe({
        // Session rejection is handled by AuthInterceptor. Network/5xx failures
        // leave the valid cookie and cached profile available for a later retry.
        error: () => {},
      });
    }
  }

  // Check if user has specific permission
  hasPermission(permission: string): boolean {
    const user = this.getUser();
    return user?.allPermissions?.includes(permission) || false;
  }

  // Check if user has any of the required permissions
  hasAnyPermission(permissions: string[]): boolean {
    const user = this.getUser();
    if (!user?.allPermissions) return false;
    return permissions.some(permission => user.allPermissions!.includes(permission));
  }

  // Check if user has al least one required permissions
  hasAllPermissions(permissions: string[]): boolean {
    const user = this.getUser();
    if (!user?.allPermissions) return false;
    return permissions.every(permission => user.allPermissions!.includes(permission));
  }

  // Upload avatar
  uploadAvatar(file: File): Observable<{ avatarUrl: string; user: User }> {
    // Frontend validation
    if (!file) {
      throw new Error('No file selected. Please choose an image file.');
    }

    if (file.size === 0) {
      throw new Error('File is empty. Please select a valid image file.');
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        'Invalid file type. Only JPEG, PNG, and WebP images are allowed. Images will be automatically converted to WebP format for optimization.'
      );
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    const formData = new FormData();
    formData.append('avatar', file);

    return this.http
      .post<{
        avatarUrl: string;
        user: User;
      }>(buildEndpointUrl(SERVICES.USERS, '/avatar'), formData)
      .pipe(
        tap(response => {
          // Update the current user with the new avatar URL and any other updated user data
          if (response.user) {
            this.setUser(response.user);
          }
        })
      );
  }

  // Forgot password
  forgotPassword(email: string): Observable<{ message: string }> {
    const forgotPasswordUrl = buildEndpointUrl(SERVICES.USERS, '/forgot-password');

    return this.http.post<{ message: string }>(forgotPasswordUrl, { email });
  }

  // Reset password
  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    const resetPasswordUrl = buildEndpointUrl(SERVICES.USERS, '/reset-password');

    return this.http.post<{ message: string }>(resetPasswordUrl, {
      token,
      password: newPassword,
    });
  }

  // Verify reset token
  verifyResetToken(token: string): Observable<{ valid: boolean }> {
    const verifyTokenUrl = buildEndpointUrl(SERVICES.USERS, '/verify-reset-token');

    return this.http.post<{ valid: boolean }>(verifyTokenUrl, { token });
  }

  // Get location for current user
  getLocation(): Observable<{ latitude: number; longitude: number } | null> {
    const locationUrl = buildEndpointUrl(SERVICES.USERS, '/location');
    return this.http.get<{ latitude: number; longitude: number } | null>(locationUrl);
  }

  // Update location for current user
  updateLocation(latitude: number, longitude: number): Observable<User> {
    const locationUrl = buildEndpointUrl(SERVICES.USERS, '/location');
    return this.http.patch<User>(locationUrl, { latitude, longitude }).pipe(
      tap(updatedUser => {
        this.setUser(updatedUser);
      })
    );
  }
}
